// Wire contract for project snapshot transfer (dev → prod).
// Mirrors docs/superpowers/specs/2026-08-05-project-snapshot-transfer-design.md
// §3 (file format), §4.2 / §4.3 (plan + apply) and §5 (diff shapes).
//
// Deliberately UUID-free, exactly like the file itself: a board is matched by
// `ext_key`, a phrase by the (category, locale, text) triple, a reward by its
// range. Nothing here carries an id from the source environment.

import type {
  Guards,
  GuildAggregator,
  LeaderboardStatus,
  Operator,
  Scope,
  Segmentation,
  Sort,
  SubmitPolicy,
  TierConfig,
  TierMode,
  Visibility,
  WindowKind,
} from "../leaderboards/types";

/**
 * The envelope's `schema` field as this build exports it. The importer rejects
 * any other value with `snapshot_unknown_schema` rather than attempting a
 * partial parse (§3) — except PRE_RHYTHM_SNAPSHOT_SCHEMA without bot settings.
 */
export const SNAPSHOT_SCHEMA = "social-hub/project-snapshot@3";

/**
 * The format before bot profiles had a play rhythm. The importer still takes
 * it when the file carries no `bot_settings` — nothing else changed — and
 * refuses it with the same bare `snapshot_unknown_schema` when it does, since
 * those profiles have no rhythm and the server invents none. Named here so the
 * import screen can say which of the two happened and what to do about it.
 */
export const PRE_RHYTHM_SNAPSHOT_SCHEMA = "social-hub/project-snapshot@2";

/**
 * The format before the bot settings left `games.config`. It is named here only
 * so the import screen can be specific about a file it refuses: a snapshot
 * taken before the move carries the whole simulation inside `config.bots`, a
 * key this build has nowhere to put, and an operator holding one needs to be
 * told to export the project again rather than «this isn't a project snapshot».
 */
export const LEGACY_SNAPSHOT_SCHEMA = "social-hub/project-snapshot@1";

// ---------------------------------------------------------------------------
// §3 — the file
// ---------------------------------------------------------------------------

/**
 * Informational provenance, shown on the import screen so a human can see at a
 * glance that they loaded the wrong file. Nothing keys off it.
 */
export interface SnapshotSource {
  /** Host header of the environment that produced the file. */
  host: string;
  /** Name of the project it was taken from — not imported, only displayed. */
  project_name: string;
}

/**
 * A reward definition as it travels. Every field of `leaderboard.Reward`
 * except `id`, `leaderboard_id` and `created_at`. Exactly one of the three
 * range shapes is populated (rank, percentile, or tier threshold), which is
 * also the tuple the importer matches on (§5.3).
 */
export interface SnapshotReward {
  tier_key?: string;
  rank_from?: number;
  rank_to?: number;
  percentile_from?: number;
  percentile_to?: number;
  name: string;
  /** Free-form JSON — the game's business, never read by the platform. */
  payload?: unknown;
  position: number;
}

/**
 * A board definition as it travels. Every field of `leaderboard.Leaderboard`
 * except `id`, `game_id`, `created_at` and `updated_at`, plus its rewards.
 * No cycles and no scores: those are environment data, not configuration.
 */
export interface SnapshotLeaderboard {
  /** The natural key boards are matched by, across every status (§5.2). */
  ext_key: string;
  name: string;
  description: string;
  scope: Scope;
  guild_aggregator?: GuildAggregator;
  operator: Operator;
  sort: Sort;
  window_kind: WindowKind;
  cron?: string;
  event_start_at?: string;
  event_end_at?: string;
  /** How long a window_kind=recurring_event board's window stays open once its
     cron starts it. Absent on every other window kind, exactly as the column
     is NULL there — so a board that travels dev→prod arrives with the same
     pause between its windows. */
  window_minutes?: number;
  segmentation: Segmentation;
  bucket_size?: number;
  tier_mode?: TierMode;
  tier_config?: TierConfig;
  realtime: boolean;
  submit_policy: SubmitPolicy;
  guards: Guards;
  visibility: Visibility;
  metadata?: unknown;
  status: LeaderboardStatus;
  /**
   * The cycle lifetime pair and the country view — configuration like every
   * field above it, and carried for the same reason: a board transferred
   * dev→prod arrives holding its players for the same term and offering the
   * same tabs.
   *
   * `hold_since` deliberately does NOT travel: it says when THIS installation
   * started holding players, which is a fact about the destination's history
   * rather than about the configuration being moved.
   */
  hold_finished_cycles?: boolean;
  retention_days?: number;
  country_split?: boolean;
  rewards: SnapshotReward[];
}

/**
 * One bot phrase. `status` sits outside the match key, so a phrase whose text
 * already exists but whose status differs is an update, not an insert (§5.4).
 */
export interface SnapshotPhrase {
  category: string;
  locale: string;
  text: string;
  status: "live" | "paused";
}

/**
 * One row of the bot source as it travels — the character an operator wrote,
 * and nothing about the environment that spent it.
 *
 * `used_bot_id`, `claimed_at`, `blocked_reason` and `position` deliberately
 * stay behind (internal/bot/snapshot_source.go): a spend link would point at a
 * player the receiving project does not have, so **every row arrives unspent**,
 * which is the whole point of carrying a roster dev → prod. The order is
 * carried implicitly instead — the order of the array is the order, and the
 * import assigns positions back from it.
 */
export interface SnapshotRosterRow {
  /** The natural key rows are matched by, on both sides. */
  name: string;
  /**
   * The asset id this row's bot will wear; absent means the row names none.
   *
   * It travels, and the receiving project's own `profile.allowed_avatars` is
   * NOT applied to it on the way in — that list belongs to whoever spends the
   * row, and is asked when the bot is created. So the sample below prints it
   * as it stands: a row whose only difference is the avatar lands in Changed,
   * and without it on the line an operator would read «changed» over two
   * samples that look identical.
   */
  avatar?: string;
  /** ISO 3166-1 alpha-2; absent leaves the choice to the locale, as today. */
  country?: string;
  /** A `bots.profile` rule key; absent means the ordinary weighted draw. */
  profile?: string;
  /** The card, held to this project's `profile.stats_schema`. */
  stats?: Record<string, unknown>;
  /** The card's meta half, held to `profile.meta_schema`. */
  meta?: Record<string, unknown>;
}

/**
 * One guild an operator described before it existed, as it travels. Same
 * omissions as SnapshotRosterRow and for the same reasons: the guild this
 * template became is a guild of the exporting environment.
 */
export interface SnapshotGuildTemplate {
  name: string;
  description?: string;
  /** The asset id the guild will wear, absent when the template names none.
   *  Nothing checks it against the receiving project's `allowed_logos`: that
   *  list is a hint the Bots page offers, not a rule any server keeps. */
  logo?: string;
  /** Held to this project's `guild.metadata_schema`, with meta semantics. */
  metadata?: Record<string, unknown>;
}

/**
 * The snapshot envelope — one JSON file.
 *
 * A section the exporter was told to leave out is **absent**, never an empty
 * array: inside a selected section the importer mirrors state to the file, so
 * an empty array would mean "delete everything" (§3).
 *
 * The bot source travels as TWO sections and not one: moving a list of guilds
 * into a project whose bot setup is entirely different is a legitimate thing to
 * want, and a checkbox has to be able to say it.
 */
export interface Snapshot {
  /** Always SNAPSHOT_SCHEMA; anything else is rejected on import. */
  schema: string;
  /** RFC3339 UTC. */
  exported_at: string;
  source: SnapshotSource;
  /** `games.config` verbatim, minus `auth.facebook.app_secret` (§2.1, §2.2). */
  config?: Record<string, unknown>;
  /**
   * `bot_settings.config` verbatim — the simulation's own document, with no
   * `bots` key wrapping it. It used to travel inside `config` above, which is
   * the whole reason this file's schema is @2 and @1 is refused.
   */
  bot_settings?: Record<string, unknown>;
  leaderboards?: SnapshotLeaderboard[];
  bot_phrases?: SnapshotPhrase[];
  bot_roster?: SnapshotRosterRow[];
  bot_guild_templates?: SnapshotGuildTemplate[];
}

// ---------------------------------------------------------------------------
// §5.1 — config diff
// ---------------------------------------------------------------------------

/**
 * How a config leaf differs.
 *  - `scalar` — two values, including a type change; `old`/`new` are compact
 *    JSON encodings.
 *  - `array`  — one row for the whole array; `old`/`new` are counts ("14 words").
 *  - `object` — two objects whose key sets differ, summarised at that level
 *    ("6 fields" → "7 fields"); the note names what was added and removed.
 *  - `locked` — the fixed `auth.facebook.app_secret` row. Informational only:
 *    it gates nothing, applies nothing, and has no `old`.
 */
export type ConfigChangeKind = "scalar" | "array" | "object" | "locked";

/** One leaf row of the recursive config walk (§5.1). */
export interface ConfigChange {
  /** Dotted path into the blob, e.g. `chat.rate_limit_per_minute`. */
  path: string;
  kind: ConfigChangeKind;
  /** Current value on this environment. Absent on `locked` rows. */
  old?: string;
  /** Value the file would install. On `locked`: "not transferred". */
  new: string;
  /** Human sentence explaining a summarised row (counts, added/removed keys). */
  note?: string;
}

export interface ConfigSection {
  /** false when the file omitted the `config` key — the section is not offered. */
  present: boolean;
  changes: ConfigChange[];
  /** Leaves that compared equal; drives the "N fields already match" line. */
  unchanged_fields: number;
}

// ---------------------------------------------------------------------------
// §5.2 / §5.3 — leaderboard diff
// ---------------------------------------------------------------------------

/**
 * What the importer would do with one board (§5.2).
 *  - `update`    — some field differs; applied now or at this board's next cut.
 *  - `create`    — the `ext_key` does not exist here; board + first cycle.
 *  - `archive`   — present here, absent from the file.
 *  - `unchanged` — everything agrees; reported for completeness only.
 *
 * There is no `recreate` and no `conflict`. Both existed because a handful of
 * fields had no write path, so a file that moved one could only drop the board
 * and build it again, or be handed back to a human. A board parks such a change
 * until its own next cut now, which leaves `ext_key` as the last field that
 * could force either — and `ext_key` is the key rows are MATCHED by, so it
 * never differs.
 */
export type BoardAction = "update" | "create" | "archive" | "unchanged";

/**
 * The two moments an import can land on. `now` is what the server reads an
 * empty string as, so the wire stays compatible with a browser that has never
 * heard of the choice.
 */
export type ApplyMode = "now" | "next_cycle";

/** Reward-level tally for one board, matched by range (§5.3). */
export interface BoardRewardCounts {
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
}

/** One row of the leaderboard table on the plan screen. */
export interface BoardChange {
  ext_key: string;
  name: string;
  action: BoardAction;
  /** Patchable fields that differ — dotted, e.g. `guards.max_score_per_submit`. */
  fields: string[];
  /**
   * The subset of `fields` that only a cut can write — the leaves the admin
   * PATCH has no slot for. A row that names any is parked whatever moment the
   * import picked, which is why its `Now` half is offered disabled.
   */
  schedule_only_fields: string[];
  /**
   * Whether this board has a cut to wait for at all: only an active recurring
   * or recurring_event board with a cycle behind it closes one. False means the
   * row lands immediately however the choice above it is set, and it is what
   * decides whether the row gets its own Now / Next cycle toggle.
   */
  schedulable: boolean;
  /**
   * When that cut falls — RFC3339, UTC — read off the board's own cycles. It is
   * the instant a parked change would land, and the screen prints it rather
   * than describing the cut in words.
   *
   * Carried only on a row that can actually wait; absent when the board has no
   * future cut on record, in which case the row names the cut it waits for
   * without a minute. Never guessed here: the file may be about to change the
   * very cron the next boundary is computed from.
   */
  next_cut_at?: string;
  /**
   * How many changes this board already has scheduled. Reported on an
   * `unchanged` row too: a board the file agrees with today will disagree with
   * it again the moment its own package applies.
   */
  pending_count: number;
  /**
   * The scheduled fields the file would overwrite. The file's values win — it
   * is the later statement about them — and the rest of the package survives.
   * `rewards` stands for the package's whole prize table.
   */
  pending_conflict_fields: string[];
  /**
   * Score rows behind the board. What decides delete versus archive, and what
   * stands behind "the rows stay where they are" on a board changing shape.
   */
  scores: number;
  /**
   * `archive` rows only: true when the board is deleted outright rather than
   * flagged `status = 'archived'`, because it holds no scores and so there is
   * no player progress to preserve.
   */
  hard_delete: boolean;
  /**
   * Absent from the file and already `status = 'archived'` here, so mirroring
   * has nothing left to do. Such rows come back as `unchanged`; the flag only
   * explains why.
   */
  already_archived?: boolean;
  rewards: BoardRewardCounts;
}

export interface LeaderboardsSection {
  /** false when the file omitted the `leaderboards` key. */
  present: boolean;
  boards: BoardChange[];
}

// ---------------------------------------------------------------------------
// §5.4 — phrase diff
// ---------------------------------------------------------------------------

/**
 * Phrases rolled up by (category, locale). Every group is returned, changed or
 * not — the UI folds the untouched ones into a single trailing row.
 */
export interface PhraseGroupChange {
  category: string;
  locale: string;
  /** How many phrases the file carries for this group. */
  in_file: number;
  /** How many exist here today. */
  here: number;
  added: number;
  deleted: number;
  /** Matched by (category, locale, text) but with a different `status`. */
  status_changed: number;
}

export interface BotPhrasesSection {
  /** false when the file omitted the `bot_phrases` key. */
  present: boolean;
  groups: PhraseGroupChange[];
}

// ---------------------------------------------------------------------------
// Bot source diff — internal/admin/snapshot/rosterdiff.go and templatediff.go
// ---------------------------------------------------------------------------

/**
 * Which of the six buckets one roster name falls into.
 *
 * FIVE of them are a PARTITION: every name on either side lands in exactly one
 * of `created`, `changed`, `removed`, `kept` and `conflict`, which is what lets
 * the card's footer print the sum as a sentence ("201 + 12 + 4 + 164 + 9
 * accounts for every row on both sides") and have it be true rather than nearly
 * true. `blocked` is the sixth and it CUTS ACROSS them — see below.
 *
 *  - `created`  — the file carries it and this project does not have it.
 *  - `changed`  — both sides have it, unspent here, and the file moves its
 *                 card, its place in the order, or its blocked state. A row a
 *                 bot stands on lands here too when the file re-orders it, and
 *                 the side to show for it is the LOCAL one: the file's card is
 *                 the one thing that will not be written.
 *  - `removed`  — an UNSPENT row this project has and the file does not. A
 *                 spent one is never removed: a living player stands behind it.
 *  - `kept`     — the file changes nothing here. Two different rows land in it
 *                 and the screen says so: a row already SPENT on a bot, which
 *                 an import may not touch at all, and an unspent row already
 *                 identical to the file's.
 *  - `blocked`  — the row arrives, and arrives blocked, because a living player
 *                 of THIS project already carries its name. Not a conflict:
 *                 applying stops for nothing, and the row is fixed on the
 *                 Source tab exactly like a collision that happened here. It is
 *                 NOT one of the five: the row it describes IS written, and is
 *                 already counted as created, changed or kept. Counting it in
 *                 the sum would count it twice, which is why `rosterAccounted`
 *                 skips this bucket and the footer's sentence has no term for
 *                 it.
 *  - `conflict` — the card names a `stat:`/`meta:` key this project's schema
 *                 does not declare. Listed, and applied by nothing.
 */
export type RosterAction =
  | "created"
  | "changed"
  | "removed"
  | "kept"
  | "blocked"
  | "conflict";

/**
 * One sample row of a bucket. It carries the whole card rather than a rendered
 * line, so the "US · level 12 · trophies 340" under a name is the card that
 * will actually be written.
 */
export interface RosterRowPlan extends SnapshotRosterRow {
  /**
   * Why this row is in its bucket, for the two where the name alone does not
   * say: it will arrive blocked, or its card was refused. Absent otherwise.
   */
  reason?: string;
  /**
   * This row will carry a blocked marker once the import has run. It is on the
   * ROW and not only in the blocked bucket because the row is in one of the
   * other five buckets too — a sample drawn under "Created" has to be able to
   * say it arrives blocked.
   */
  blocked?: boolean;
}

/** One bucket: how many rows are in it, and a couple of them. */
export interface RosterGroupPlan {
  action: RosterAction;
  count: number;
  /**
   * At most two rows (`maxRosterSamples`), so a bucket of two hundred costs the
   * same bytes as a bucket of two. The "…and N more" line under them is
   * `count - samples.length`, which is why both numbers come from one place.
   */
  samples: RosterRowPlan[];
}

export interface BotRosterSection {
  /** false when the file omitted the `bot_roster` key. */
  present: boolean;
  /** How many rows the FILE carries — "217 rows in the file". */
  rows: number;
  /** All six buckets, zero ones included, in the order they are drawn. */
  groups: RosterGroupPlan[];
}

/**
 * The roster's buckets, one level up. Same meanings, about a guild — minus
 * `blocked`, which is never an ACTION on a template row: the template is
 * written, and it is written as a created, changed or kept row. Whether it
 * arrives blocked is the flag below, drawn beside the action rather than
 * instead of it.
 */
export type TemplateAction = Exclude<RosterAction, "blocked">;

/**
 * One row of the guild-template table. Templates are listed one by one rather
 * than bucketed: a project has as many as it means to have guilds on day one,
 * and an operator recognises every one of them by name.
 *
 * `here` and `in_file` are the two sides as the screen prints them, and `null`
 * is the "—" in either column: a created template has no `here`, a removed one
 * has no `in_file`.
 */
export interface GuildTemplatePlan {
  name: string;
  action: TemplateAction;
  here: SnapshotGuildTemplate | null;
  in_file: SnapshotGuildTemplate | null;
  /** Why, for the two cases where the name alone does not say: it will arrive
   *  blocked, or its metadata was refused. */
  reason?: string;
  /** A guild of this project already carries the name, so the template arrives
   *  — or stays — blocked. Beside the action, never one of its values. */
  blocked?: boolean;
}

export interface BotGuildTemplatesSection {
  /** false when the file omitted the `bot_guild_templates` key. */
  present: boolean;
  /** How many templates the FILE carries. */
  rows: number;
  templates: GuildTemplatePlan[];
}

// ---------------------------------------------------------------------------
// §4.2 — POST /snapshot/plan
// ---------------------------------------------------------------------------

/** The four headline tiles plus the per-section numbers behind them (§5.5). */
export interface PlanSummary {
  /** Config fields + boards updated + phrase groups + roster rows + templates. */
  changed: number;
  /** Boards + phrases + roster rows + guild templates created. */
  created: number;
  /** Boards archived + phrases, roster rows and templates deleted. */
  removed: number;
  /**
   * Everything the importer will skip: a roster row or a template naming a card
   * key this project's schema does not declare. No board is ever in here — a
   * board that used to be a conflict is an ordinary update that waits for a cut.
   */
  conflicts: number;

  config_fields: number;
  bot_settings_fields: number;
  boards_changed: number;
  phrase_groups_changed: number;
  roster_rows_changed: number;
  guild_templates_changed: number;
  boards_created: number;
  phrases_added: number;
  roster_rows_created: number;
  guild_templates_created: number;
  boards_archived: number;
  phrases_deleted: number;
  roster_rows_removed: number;
  guild_templates_removed: number;
  /**
   * The two blocked counts are under no tile, and they OVERLAP the numbers
   * above: a blocked row is written like any other — it is a state of the row
   * and not a refusal — so it is already inside `*_created` or `*_changed`, and
   * putting it under a tile would count it twice. It is reported beside them
   * because "201 arrived" and "7 of them cannot be spent until somebody renames
   * them" are different facts, and it equals the apply's own `*_blocked` for
   * the same file.
   */
  roster_rows_blocked: number;
  guild_templates_blocked: number;

  /**
   * How many of `boards_changed` have a cut to wait for — the "N board updates
   * can wait" the choice card leads with. It OVERLAPS `boards_changed` for the
   * reason the two blocked counts overlap their tiles: it describes a property
   * of rows already counted, not extra work.
   */
  boards_schedulable: number;
  /**
   * How many boards of this project already hold a scheduled package, whatever
   * the file says about them — an unchanged board with one included, because
   * after its package applies it parts company with the file again. A package
   * is bound to another environment's cycle boundary and to an admin the
   * receiving project does not have, so it never travels in a file.
   */
  boards_with_pending_changes: number;
}

/**
 * The server-computed plan. Computed server-side on every call and never in
 * the browser — only the server knows whether a board holds scores.
 */
export interface SnapshotPlan {
  /** The project's current etag; echoed back on apply to detect divergence. */
  etag: string;
  source: SnapshotSource;
  exported_at: string;
  schema: string;
  /** `source.project_name` vs this project's name. A warning, never a block. */
  project_name_matches: boolean;
  summary: PlanSummary;
  config: ConfigSection;
  /** Shaped exactly like `config`, because it is the same kind of thing: one
   *  document, diffed leaf by leaf, applied whole. Its paths are read from its
   *  OWN root — `population.target_members`, with no `bots.` in front. */
  bot_settings: ConfigSection;
  leaderboards: LeaderboardsSection;
  bot_phrases: BotPhrasesSection;
  bot_roster: BotRosterSection;
  bot_guild_templates: BotGuildTemplatesSection;
}

// ---------------------------------------------------------------------------
// §4.3 — POST /snapshot/apply
// ---------------------------------------------------------------------------

/** Which sections the human left checked. An unchecked section is untouched. */
export interface ApplySelection {
  config: boolean;
  bot_settings: boolean;
  leaderboards: boolean;
  /** The boards whose rows stayed checked. */
  leaderboard_keys: string[];
  /**
   * The default moment for every board the plan updates. It says nothing about
   * the rest of the file: a create, an archive and the config, bots and phrase
   * sections have no cycle whose boundary they could wait for.
   */
  apply_mode: ApplyMode;
  /**
   * Overrides it for one board, keyed by `ext_key`. A weekly board and a daily
   * one cut at different moments, so there is no single instant the whole file
   * could land on — the choice is atomic per board.
   */
  board_apply_mode?: Record<string, ApplyMode>;
  bot_phrases: boolean;
  /**
   * Two checkboxes and not one: moving a list of guilds into a project with an
   * entirely different bot setup is a legitimate thing to want.
   */
  bot_roster: boolean;
  bot_guild_templates: boolean;
}

export interface ApplyRequest {
  /** The same envelope that produced the plan; the server re-diffs it. */
  snapshot: Snapshot;
  selection: ApplySelection;
  /** From SnapshotPlan.etag. A mismatch is a 409 `etag_mismatch`. */
  etag: string;
}

/**
 * Result of a committed apply. Everything selected lands in one transaction,
 * so these counts describe a state that is already durable.
 */
export interface ApplyResult {
  /** Total changes committed — the "Snapshot applied · N changes" toast. */
  applied: number;
  /** The project's etag after the transaction, so the page can keep working. */
  etag: string;
  /** Per-section breakdown, mirroring the activity entry's `details`. */
  config_fields?: number;
  bot_settings_fields?: number;
  boards_created?: number;
  boards_updated?: number;
  /**
   * Boards that took their update as a package instead. Counted beside
   * `boards_updated` rather than inside it — "players see this now" and
   * "players see this at that board's next cut" are different promises — and
   * summed into `applied`, because the package is as durable as any other row
   * the transaction wrote.
   */
  boards_scheduled?: number;
  boards_archived?: number;
  phrases_added?: number;
  phrases_updated?: number;
  phrases_deleted?: number;
  roster_rows_added?: number;
  roster_rows_updated?: number;
  roster_rows_deleted?: number;
  guild_templates_added?: number;
  guild_templates_updated?: number;
  guild_templates_deleted?: number;
  /**
   * Rows the write treated specially rather than work it did, so they are
   * reported and NOT summed into `applied`: a blocked row is already inside
   * added or updated, and a skipped one was written by nothing at all.
   */
  roster_rows_blocked?: number;
  roster_rows_skipped?: number;
  guild_templates_blocked?: number;
  guild_templates_skipped?: number;
}

// ---------------------------------------------------------------------------
// §4.1 — GET /snapshot
// ---------------------------------------------------------------------------

/**
 * Which sections the export should carry. A `false` omits that key from the
 * envelope entirely (query param `<section>=0`); the default is all five.
 */
export interface SnapshotSections {
  config: boolean;
  /** `bot_settings` — omitted when empty as well as when unticked: mirroring an
   *  empty document would reset the receiving project to the platform defaults,
   *  which is not what any checkbox on that screen says. */
  bot_settings: boolean;
  leaderboards: boolean;
  phrases: boolean;
  /** `bot_roster` — the query param is `roster`, as the handler reads it. */
  roster: boolean;
  guild_templates: boolean;
}
